<template>
  <div class="quote-tool-step-content">
    <div class="d-flex justify-start">
      <v-btn
        color="blue"
        text
        class="px-0"
        variant="text"
        @click="showContainerSizeDescriptionDialog = true">Which size is right for me?</v-btn>
    </div>
    <v-dialog v-model="showContainerSizeDescriptionDialog" :max-width="800"  style="z-index:9999;" content-class="container-size-dialog"  >
      <v-card class="pa-7" style="overflow: auto; height:550px">
        <section>
          <v-card-title class="headline text-decoration-underline">
            15-Yard Dumpster
          </v-card-title>
          <v-card-text>
            <img src="https://nextdaydumpsters.com/wp-content/uploads/2024/11/15-yard-1.png" />
            <p>A 15-yard dumpster is the smallest size we offer in most markets and can comfortably fit in most residential driveways and job sites.</p>
            <p>You can put about 6 pickup truck loads worth of material into this dumpster.</p>
            <p>This is a good option when you have a small project, lighter material, or have a tight space for the dumpster to be placed.</p>
          </v-card-text>
        </section>
        <v-divider class="my-7"/>
        <section>
          <v-card-title class="headline text-decoration-underline">
            20-Yard Dumpster
          </v-card-title>
          <v-card-text>
            <img src="https://nextdaydumpsters.com/wp-content/uploads/2024/11/20-yard-1.png" />
            <p>A 20-yard dumpster is a good option when you want extra room and peace of mind to make sure your debris fits along with a higher weight allowance (the amount included in your rental before you're charged overweight rates).</p>
            <p>It can fit in most residential driveways and job sites and holds about 8 pickup truck loads worth of material and is the equivalent of filling a one-car garage about halfway to the top.</p>
          </v-card-text>
        </section>
        <v-divider class="my-7"/>
        <section>
          <v-card-title class="headline text-decoration-underline">
            30-Yard Dumpster
          </v-card-title>
          <v-card-text>
            <img src="https://nextdaydumpsters.com/wp-content/uploads/2024/11/30-yard-1.png" />
            <p>A 30-yard dumpster is the largest size we offer and has our highest standard weight allowance. It fits about 12 pickup truck loads worth of material or the equivalent of filling a one-car garage from floor to ceiling.</p>
            <p>This dumpster might be too large for residential driveways or tight spaces, so make sure to measure your available space if you’re planning to pick this option (20 ft. long x 8 ft. wide).</p>
            <p>This is a good option when you have projects that span multiple rooms, heavier materials, and ample space.</p>
          </v-card-text>
        </section>
        <div class="card-footer d-flex justify-end">
          <v-btn tile color="secondary" @click="showContainerSizeDescriptionDialog = false">
            Close
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
    <v-form ref="containerSizeForm">
      <v-expand-transition>
        <v-radio-group
          v-model="containerType"
          :mandatory="false"
          :rules="$validationRules.required"
          id="container-type-radio-group"
          tabindex="3"
        >
          <v-card
            v-for="item in containerTypes"
            :key="item.id"
            class="my-1"
            @click="containerType = item.id"
            @keyup.enter="containerType = item.id"
            @keyup.space="containerType = item.id"
          >
            <v-card-text class="d-flex">
              <v-radio
                class="mb-0 container-type-radio"
                :value="item.id"
              >
              <template #label>
                <span class=" black--text">{{ item.description }}</span>
              </template>
            </v-radio>
              <v-spacer></v-spacer>
              <span class="d-none d-md-flex align-self-center" v-if="item.dimensions">{{
                item.dimensions
              }}</span>
            </v-card-text>
            <v-card-text class="d-flex justify-space-between d-md-none py-0">
              <div
                v-if="item.image_url"
                class="image container-image d-flex justify-start align-start me-3"
              >
                <img :src="item.image_url" />
              </div>
              <span class="align-self-center" v-if="item.dimensions">{{
                item.dimensions
              }}</span>
            </v-card-text>
            <v-card-text class="d-flex justify-space-between pt-0">
              
              <div class="text px-2">
                <p style="font-size:0.95rem;" class="mb-0 pr-2" v-if="item.hint">
                  {{ item.hint }}
                </p>
              </div>
              <div
                v-if="item.image_url"
                class="image d-none d-md-flex justify-end align-end"
              >
                <img height="55px" :src="item.image_url" />
              </div>
            </v-card-text>
            <template v-if="showPrices">
              <v-divider class="mb-0"></v-divider>

              <v-card-text class="d-flex justify-end">
                <div class="text px-2 align-self-end">
                  <b>Price Estimate: </b>${{
                    item.price_groups && item.price_groups[0].price
                  }}
                </div>
              </v-card-text>
            </template>
          </v-card>
        </v-radio-group>
      </v-expand-transition>
    </v-form>
    <PublicStepControls
      :canDelete="true"
      :nextStepEnabled="hasStepBeenCompleted(stepObject.component) && allowNextStep"
      :nextStep="submit"
      :previousStep="previousStep"
      previousStepTitle="Change Debris Type"
    >
    </PublicStepControls>
  </div>
</template>
<script>
import { createNamespacedHelpers, mapMutations, mapGetters } from "vuex";
import { mapFields } from "vuex-map-fields";
import { UPDATE_STEP_METADATA } from "@/store/modules/quotes/mutation-types";

import PublicStepControls from "../PublicStepControls.vue";
const { mapGetters: mapQuoteGetters } = createNamespacedHelpers("quotes");

/**
 * @typedef {int} isService
 * @typedef {bool} validLocation
 * */
export default {
  name: "ProjectTypeStep",
  props: ["nextStep", "previousStep", "stepObject"],
  components: {
    PublicStepControls,
  },
  computed: {
    ...mapFields("quotes", {
      containerType: "quote.service.containerType",
      weightLimit: "quote.service.weightLimit",
      rentalPeriod: "quote.service.rentalPeriod",
      serviceSelectionId: "serviceSelectionId"
    }),
    ...mapFields("quotes", ["isService", "quote", "loading"]),
    ...mapGetters("quotes", [
      "userAccess",
      "validPartialLocation",
      "validLocation",
      "containerTypes",
      "hasStepBeenCompleted"
    ]),
    allowNextStep() {
      return this.valid;
    },
    ...mapQuoteGetters([
      "containerTypes",
      "containerTypes",
      "catalogDetails",
      "terms",
      "containerTypes"
    ]),
    containerTypeName() {
      return (
        this.containerTypes.find(d => d.id === (this.containerType || "")) || {
          description: "N/A"
        }
      ).description;
    }
  },
  methods: {
    ...mapMutations("quotes", [UPDATE_STEP_METADATA]),
    updateStepContinue(val) {
      // Set whether this step can continue or not
      this[UPDATE_STEP_METADATA]({
        component: this.$options.name,
        canContinue: val
      });
    },
    submit() {
      // TODO: Determine why this is not working without a timeout
      // Wait 0.25 seconds
      setTimeout(() => {
        if (this.allowNextStep && this.$refs.containerSizeForm.validate()) {
          this.nextStep();
        }
      }, 250);
    }
  },
  data() {
    return {
      valid: true,
      showPrices: false,
      showContainerSizeDescriptionDialog: false,
    };
  },
  watch: {
    allowNextStep(newVal) {
      // If this step can continue, the the next step should be marked as editable
      this.updateStepContinue(newVal);
    },
    containerType(newVal) {
      // next step
      if (newVal) {
         // clear any previously selected modifiers
        this.weightLimit = 0
        this.rentalPeriod = 0
        this.updateStepContinue(true);
        this.submit();
      }
    }
  },
  mounted() {
    // Same as watcher
    this.updateStepContinue(this.allowNextStep);
  }
};
</script>
<style>

.container-size-dialog{
  margin-top:50px;
}

.container-size-dialog img{
  max-width: 350px;
}

.container-image img{
    max-width: 300px;
}

.container-type-radio-group p{
  font-size:2rem;
}

@media screen and (max-width: 480px) {
  .container-image img{
    max-width: 125px !important;
  }

  .container-size-dialog img{
    max-width: 200px;
  }

  .container-size-dialog .headline{
    font-size: 1.2rem !important;
  }
}

@media screen and (max-width: 768px) {
  .container-image img{
    max-width: 250px;
  }
}


</style>
